import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../../network/api'
import { Button } from '../../components/Button'
import { isInNativeApp, postMessageToNative } from '../../services/nativeBridge'
import { toast } from '../../services/toastService'

interface MediaDTO {
  id: number
  path: string
  type: string
}

interface CustomDeckCoverDTO {
  id: number
  name: string
  originalUniqueId: string
  originalMedia?: MediaDTO
  modelMedia?: MediaDTO
  coverMedia?: MediaDTO
  croppedMedia?: MediaDTO
  croppedInfo: string
  createTime: string
  updateTime: string
}

function ListItemImage({ src, alt }: { src: string; alt: string }) {
  const [error, setError] = useState(false)
  const [errorDetail, setErrorDetail] = useState<string>('')

  const handleErrorClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    toast.error(`图片加载失败: ${errorDetail || '未知错误'}\n路径: ${src}`)
  }

  if (error) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-red-500/40 bg-red-500/5 gap-2 cursor-pointer hover:bg-red-500/10 transition-colors"
        onClick={handleErrorClick}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span className="text-[10px] whitespace-pre-wrap break-all px-2 text-center">{`${errorDetail || '加载失败'}\n路径: ${src}`}</span>
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover transition-opacity duration-300"
      onError={() => {
        setError(true)
        // 尝试记录一些上下文信息
        const msg = !navigator.onLine ? '网络连接已断开' : '资源不存在或无权访问'
        setErrorDetail(msg)
      }}
      onLoad={(e) => (e.currentTarget.style.opacity = '1')}
      style={{ opacity: 0 }}
    />
  )
}

export function CustomDeckCoverListPage() {

  const [list, setList] = useState<CustomDeckCoverDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await api.custom_deck_cover.setGET().request()
      if (res && res.data && res.data.contents) {
        setList(res.data.contents)
      }
      console.log('CustomDeckCoverPage list:', res)
    } catch (error) {
      console.error('Fetch list failed:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    document.title = '我的设计'
  }, [])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const handleSelect = (item: CustomDeckCoverDTO) => {
    setSelectedId(item.id)
  }

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('确定要删除这个设计吗？')) return
    try {
      await api.custom_deck_cover.setOther(id.toString()).setDELETE().request()
      if (selectedId === id) setSelectedId(null)
      fetchList()
      toast.success('删除成功')
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('删除失败')
    }
  }

  const handleEdit = (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    navigate(`/custom-deck-cover?id=${id}`)
  }

  const handleBuy = () => {
    if (!selectedId) return
    const index = list.findIndex(i => i.id === selectedId)
    if (index === -1) return
    const item = list[index]
    console.log('handleBuy > ', item)
    if (isInNativeApp()) {
      let targetData = {
        specName1: item?.name ?? `图案（${index + 1}）`
      }
      postMessageToNative('doAction', {
        action: 'open_goods',
        targetId: 565,
        targetData: JSON.stringify(targetData)
      })
    } else {
      toast.info('请在 App 中购买此设计')
    }
  }

  const handleBack = () => {
    // By checking if location.state.from is '/custom-deck-cover'
    if (location.state?.from === '/custom-deck-cover') {
      navigate(-1)
    } else {
      // Fallback: if not entered from /custom-deck-cover, go to it with replace: true
      navigate('/custom-deck-cover', { replace: true })
    }
  }

  return (
    <div className="flex-1 bg-black text-white p-4 pb-[calc(6.2rem+env(safe-area-inset-bottom))]">
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
          </div>
        ) : list.length === 0 ? (
          <div className="text-white/40 text-center flex flex-col items-center justify-center py-20 gap-2">
            <p className="mb-4">暂无保存的设计</p>
            <div className="flex justify-center items-center gap-2">
              <Button variant="primary" onClick={handleBack}>去创作</Button>
              <Button variant="secondary" onClick={fetchList}>
                刷新
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {list.map((item) => (
              <div
                key={item.id}
                className={`group relative aspect-3/4 bg-white/5 rounded-xl overflow-hidden cursor-pointer transition-all border ${selectedId === item.id ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-white/10 hover:border-white/30'
                  }`}
                onClick={() => handleSelect(item)}
              >
                {item.coverMedia?.path ? (
                  <ListItemImage
                    src={item.coverMedia.path}
                    alt="design"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/20 gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                    <span className="text-xs">无预览图</span>
                  </div>
                )}

                {/* Hover actions */}
                <div className={`absolute top-2.5 right-2.5 flex flex-col gap-2 transition-opacity z-10 ${selectedId === item.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}>
                  <button
                    onClick={(e) => handleEdit(e, item.id)}
                    className="p-2 bg-black/60 hover:bg-black/80 rounded-full text-white backdrop-blur-sm transition-colors"
                    title="编辑"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, item.id)}
                    className="p-2 bg-red-500/60 hover:bg-red-500/80 rounded-full text-white backdrop-blur-sm transition-colors"
                    title="删除"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" />
                    </svg>
                  </button>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-3 bg-linear-to-t from-black/80 to-transparent translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                  <div className="text-xs text-white/60 mb-1">
                    {new Date(item.createTime).toLocaleDateString()}
                  </div>
                  <div className="text-sm font-medium truncate">
                    Design #{item.id}
                  </div>
                </div>

                {selectedId === item.id && (
                  <div className="absolute top-3 left-3 p-1 bg-blue-500 rounded-full text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}

                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sticky Bottom Buy Button */}
      <div className="fixed bottom-0 inset-x-0 p-4 pb-[max(1.2rem,env(safe-area-inset-bottom))] bg-black/80 backdrop-blur-md border-t border-white/10 flex justify-center items-center z-20">
        <div className="max-w-4xl w-full">
          <Button
            variant="primary"
            fullWidth
            size="lg"
            disabled={!selectedId}
            onClick={handleBuy}
          >
            {selectedId ? '购买此板面' : '请先选择一个设计'}
          </Button>
        </div>
      </div>
    </div>
  )
}

